// src/App.js
import React, { useContext } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Header from './components/Header';
import Home from './components/Home';
import Footer from './components/Footer';
import Customization from './components/Customization';
import Contact from './components/Contact';
import VersionNotes from './components/VersionNotes';
import ThemeContext, { ThemeProvider } from './context/ThemeContext';
import styled, { ThemeProvider as StyledThemeProvider, createGlobalStyle } from 'styled-components';

{/* Global styles for the app */}
const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${({ theme }) => theme.mainBody};
    color: ${({ theme }) => theme.mainField};
    margin: 0;
    padding: 0;
  }
`;

{/* Main app container with flexbox to structure the header, scrollable content, and footer */}
const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh; 
`;

{/* Header placeholder to prevent overlap */}
const HeaderPlaceholder = styled.div`
  height: 65px; /* Same height as the header */
  flex-shrink: 0;
`;

{/* Footer placeholder to prevent overlap */}
const FooterPlaceholder = styled.div`
  height: 21px; /* Same height as the footer */
  flex-shrink: 0;
`;

{/* Scrollable content container between the header and footer */}
const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 21px;

  /* Scrollbar styles for the scrollable container */
  ::-webkit-scrollbar {
    width: 3px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.mainAccent};
    border-radius: 1px;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }

  &:hover ::-webkit-scrollbar-thumb,
  &:active ::-webkit-scrollbar-thumb {
    opacity: 1;
  }

  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => `${theme.mainAccent} transparent`};
`;

const App = () => {
  return (
    <ThemeProvider> {/* Wrapping the entire app in ThemeProvider */}
      <ThemedAppContent />
    </ThemeProvider>
  );
};

{/* This component is rendered after the ThemeProvider is in place */}
const ThemedAppContent = () => {
  const { theme } = useContext(ThemeContext);

  return (
    <StyledThemeProvider theme={theme}>
      <GlobalStyle />
      <Router>
        <AppContainer>
          <Header />
          <HeaderPlaceholder />
          <ScrollableContent theme={theme}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/customization" element={<Customization />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/version-notes" element={<VersionNotes />} />
              <Route path="*" element={<Home />} />
            </Routes>
          </ScrollableContent>
          <FooterPlaceholder />
          <Footer/>
        </AppContainer>
      </Router>
    </StyledThemeProvider>
  );
};

export default App;
